import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';
import useFormatMessage from 'hooks/useFormatMessage';
import useMatomoGoalOptions, {
  collectConversionGoalIds,
  useMatomoGoalNamesByIds,
} from 'hooks/useMatomoGoalOptions';
import { combineQueries, isQueryEmpty } from 'shared/utils/queryBuilder';
import { getQuerySummary } from 'components/GlobalFilter/FilterSegment';

const SEGMENTS_QUERY = gql`
  query Segments {
    segments {
      id
      title
      query
    }
  }
`;

export function useResultFilterSummary({ queryFilter, scopeFilter, runtimeQueryFilter, customerId }) {
  const $t = useFormatMessage();
  const { data } = useQuery(SEGMENTS_QUERY);
  const combinedQuery = useMemo(
    () => combineQueries(queryFilter, runtimeQueryFilter),
    [queryFilter, runtimeQueryFilter],
  );
  const filterGoalIds = useMemo(() => collectConversionGoalIds(combinedQuery), [combinedQuery]);
  const { goalById: filterGoalById, loading: filterGoalsLoading } = useMatomoGoalNamesByIds(filterGoalIds);
  const { goalById: customerGoalById, loading: customerGoalsLoading } = useMatomoGoalOptions(customerId);

  const goalById = useMemo(
    () => ({ ...customerGoalById, ...filterGoalById }),
    [customerGoalById, filterGoalById],
  );
  const goalLoading = filterGoalsLoading || customerGoalsLoading;

  const querySummary = useMemo(() => getQuerySummary({
    query: combinedQuery,
    segments: data?.segments,
    $t,
    goalById,
    goalLoading,
  }), [combinedQuery, data?.segments, $t, goalById, goalLoading]);

  const hasQueryFilter = !isQueryEmpty(combinedQuery) && !!querySummary;
  const hasScopeFilter = !!scopeFilter;

  const summary = useMemo(() => {
    const parts = [];
    if (hasScopeFilter) {
      parts.push(scopeFilter.title ? `Scope: ${scopeFilter.title}` : 'Custom scope override');
    }
    if (hasQueryFilter) {
      parts.push(querySummary);
    }
    return parts.join('\n');
  }, [hasScopeFilter, hasQueryFilter, scopeFilter?.title, querySummary]);

  return {
    summary,
    hasFilter: hasScopeFilter || hasQueryFilter,
    hasQueryFilter,
    hasScopeFilter,
  };
}
